import React, { useState, useRef, useEffect } from 'react';

const C = {
  surface: '#0B1120', card: '#111B2E',
  border: 'rgba(255,255,255,0.07)', white: '#F2F6FA',
  muted: '#AAB8C8', fog: '#667788', teal: '#00D4C8',
};

// ─── InfoTip: hover on desktop, tap on mobile ───────────────────────────────
export function InfoTip({ text, title, width = 260, position = 'top' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click (mobile)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [open]);

  const isMobile = window.innerWidth < 768;

  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;

  const popoverStyle = {
    position: 'absolute',
    width: isMobileView ? Math.min(width, window.innerWidth - 32) : width,
    background: C.card,
    border: '1px solid #F5C84240',
    borderRadius: 10,
    padding: '10px 12px',
    zIndex: 99999,
    boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
    pointerEvents: 'none',
    textAlign: 'left',
    // On mobile always position above and left-anchored to avoid going off screen
    ...(isMobileView
      ? { bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' }
      : position === 'top' ? { bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' }
      : position === 'bottom' ? { top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)' }
      : position === 'left' ? { right: 'calc(100% + 8px)', top: '50%', transform: 'translateY(-50%)' }
      : { left: 'calc(100% + 8px)', top: '50%', transform: 'translateY(-50%)' }
    ),
  };

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}
      onMouseEnter={() => !isMobile && setOpen(true)}
      onMouseLeave={() => !isMobile && setOpen(false)}
      onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 15, height: 15, borderRadius: '50%',
        border: `1px solid #F5C84280`,
        color: '#F5C842', fontSize: 9, fontWeight: 700,
        cursor: 'pointer', marginLeft: 5, flexShrink: 0,
        fontFamily: "'Inter', sans-serif",
        userSelect: 'none',
      }}>ⓘ</span>

      {open && (
        <span style={popoverStyle}>
          {title && <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#F5C842', fontWeight: 700, letterSpacing: '0.01em', marginBottom: 5 }}>{title}</p>}
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.75, margin: 0, fontFamily: "'Inter', sans-serif", textAlign: 'left' }}>{text}</p>
        </span>
      )}
    </span>
  );
}

// ─── FirstVisitBanner: slim one-time banner on first demo visit ─────────────
export function FirstVisitBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('is_demo') !== 'true') return;
    if (localStorage.getItem('demo_explainer_shown') === 'true') return;
    setVisible(true);
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Dark backdrop */}
      <div onClick={() => setVisible(false)} style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(3,5,10,0.75)',
      }} />
      {/* Centre card */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1001,
        width: 'min(480px, 90vw)',
        background: C.surface,
        border: '1px solid #F5C84250',
        borderRadius: 20,
        padding: '32px 28px',
        fontFamily: "'Inter', sans-serif",
        boxShadow: '0 24px 80px rgba(0,0,0,0.8)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>👋</div>
        <p style={{ fontSize: 20, fontWeight: 700, color: C.white, marginBottom: 10, lineHeight: 1.3 }}>
          Welcome to reAImagination
        </p>
        <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.75, marginBottom: 24 }}>
          Every screen is interactive and explained. Look for the <strong style={{ color: '#F5C842', fontFamily: "'Inter', sans-serif" }}>ⓘ</strong> icon next to any number or term - tap or hover for a plain-language explanation of what you are looking at and why it matters.
        </p>
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: '12px 16px', marginBottom: 24, textAlign: 'left',
        }}>
          {[
            { icon: '📡', text: 'Signal Room - your transformation gap, quantified' },
            { icon: '🔬', text: 'Analysis - your roles, deconstructed task by task' },
            { icon: '🧠', text: 'Intelligence - function-level AI opportunity map' },
            { icon: '🗺️', text: 'Blueprint - your transformation roadmap' },
            { icon: '🎯', text: 'Scenarios - model and compare transformation strategies' },
            { icon: '🎭', text: 'The Stage - fractional talent matched to your roles' },
            { icon: '✨', text: 'Showcase - AI tools matched to your bot opportunities' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: i < 6 ? 10 : 0 }}>
              <span style={{ fontSize: 14, flexShrink: 0, paddingTop: 1 }}>{item.icon}</span>
              <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, margin: 0 }}>{item.text}</p>
            </div>
          ))}
        </div>
        <button onClick={() => { setVisible(false); localStorage.setItem('demo_explainer_shown', 'true'); }} style={{
          width: '100%', padding: '13px',
          background: C.teal, border: 'none', borderRadius: 10,
          color: C.bg, fontSize: 14, fontWeight: 700, cursor: 'pointer',
        }}>
          Got it - show me the platform →
        </button>
      </div>
    </>
  );
}

// ─── Legacy DemoExplainer - now a no-op (replaced by InfoTip) ───────────────
export default function DemoExplainer() { return null; }
